import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { get, merge } from "lodash";
// import Content from "features/Settings/sections/General/Notifications/Content";
import { useProfile } from "../../hooks/useProfile";
import useApi from "utils/api";
import { usePreferenceMutation } from "features/Settings/Configuration/api/usePreferenceMutation";
import { useCompany } from "utils/hooks/company";
import { preferenceQueryKeys } from "features/Settings/Configuration/api/usePreferenceQuery";
import { config } from "features/Settings/General/Notifications/Content/config";

function NotificationSettings() {
    const { user, isSelf } = useProfile();
    const company = useCompany();
    const { authGet } = useApi();

    const { data: _mainCompanyPreferences } = useQuery({
        queryKey: ["mainCompanyPreferences", company?.id, (company as any)?.masterCompanyId],
        queryFn: () => {
            if (!company) return null;
            const url = company.isMasterCompany
                ? `/preferencies/company/${company.id}?preferenceKey=notifications`
                : `/preferencies/company/${(company as any).masterCompanyId}?preferenceKey=notifications`;
            return authGet(url);
        },
        enabled: Boolean(company),
    });

    const { data: _companyPreferencesRaw } = useQuery({
        queryKey: preferenceQueryKeys.company(company?.id as string),
        queryFn: () => authGet(`/preferencies/company/${company?.id}?preferenceKey=notifications`),
        enabled: !!company?.id,
    });

    const { data: userPreferencesRaw, isLoading } = useQuery({
        queryKey: [...preferenceQueryKeys.user(user?.id as string), "notifications"],
        queryFn: () => authGet(`/preferencies/user/${user?.id}`),
        enabled: !!user?.id,
    });

    const mutation = usePreferenceMutation("user", "notifications", user?.id as string);

    const form = useForm();

    const onFinish = async (value: any) => {
        await mutation.mutateAsync(value);
    };

    const mainCompanyPreferences = useMemo(() => {
        return _mainCompanyPreferences?.result
            ?.find((item: any) => item.preferenceKey === "notifications")
            ?.config;
    }, [_mainCompanyPreferences]);

    const companyPreferencesRaw = useMemo(() => {
        return _companyPreferencesRaw?.result
            ?.find((item: any) => item.preferenceKey === "notifications")
            ?.config;
    }, [_companyPreferencesRaw]);

    useEffect(() => {
        const userConfig = userPreferencesRaw?.result
            ?.find((item: any) => item.preferenceKey === "notifications")
            ?.config;

        const initialValues = Object.entries(config).reduce((total: any, [key, sections]) => {
            total[key] = Object.entries(sections).reduce((_total: any, [sectionKey, items]: any) => {
                return {
                    ..._total,
                    [sectionKey]: items.reduce((acc: any, item: any) => {
                        const main = mainCompanyPreferences ? get(mainCompanyPreferences, [key, sectionKey, item.type], null) : null;
                        const company = companyPreferencesRaw ? get(companyPreferencesRaw, [key, sectionKey, item.type], null) : null;
                        const user = userConfig ? get(userConfig, [key, sectionKey, item.type], null) : null;

                        if (main?.force) {
                            return { ...acc, [item.type]: main };
                        }
                        if (company?.force) {
                            return { ...acc, [item.type]: company };
                        }

                        const defaults: any = {};
                        if ("email" in item) defaults.email = false;
                        if ("push" in item) defaults.push = false;
                        if (item.duration) defaults.value = null;
                        if (item.day) defaults.day = "sunday";
                        defaults.force = false;

                        const saved = user || company || main || {};

                        return { ...acc, [item.type]: merge(defaults, saved) };
                    }, {}),
                };
            }, {});
            return total;
        }, {});

        if (!isLoading) {
            form.reset(initialValues);
        }
    }, [
        form,
        mainCompanyPreferences,
        companyPreferencesRaw,
        userPreferencesRaw?.result,
        isLoading
    ]);

    return (
        <div className="tw-border tw-rounded-xl tw-p-5">
            {/* <Content
                page={isSelf ? "my-profile" : "profile"}
                form={form}
                mainCompanyPreferences={mainCompanyPreferences}
                companyPreferences={companyPreferencesRaw}
                user={user}
                submitting={mutation.isPending}
                loading={isLoading}
            /> */}
        </div>
    );
}

export default NotificationSettings;
